export interface DayGroup<TItem> {
  /** Local `YYYY-MM-DD`, or `__no-date__` for items whose timestamp is missing/unparseable. */
  key: string;
  /** Display label resolved by the caller (Today / Yesterday / formatted date). Empty for the no-date group. */
  label: string;
  items: TItem[];
}

export interface GroupByDayOptions<TItem> {
  getTimestamp: (item: TItem) => string | null | undefined;
  todayLabel: string;
  yesterdayLabel: string;
  /** Formats a **local** `YYYY-MM-DD` key into the locale-aware label for older days. */
  formatDate: (isoDate: string) => string;
}

const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const toDayKey = (date: Date): string => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Buckets items by calendar day, preserving input order within each group. Assumes the input is already
 * sorted newest-first.
 *
 * Day keys are built from local date parts, not from `toISOString()`: a UTC key shifts the boundary and files
 * late-evening rows under the next day for negative-offset timezones.
 */
export function groupByDay<TItem>(items: TItem[], options: GroupByDayOptions<TItem>): DayGroup<TItem>[] {
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  const buckets = new Map<string, DayGroup<TItem>>();

  const push = (key: string, label: string, item: TItem) => {
    const existing = buckets.get(key);
    if (existing) existing.items.push(item);
    else buckets.set(key, { key, label, items: [item] });
  };

  for (const item of items) {
    const raw = options.getTimestamp(item);
    const date = raw ? new Date(raw) : null;
    if (!date || Number.isNaN(date.getTime())) {
      push("__no-date__", "", item);
      continue;
    }

    const dayKey = toDayKey(date);
    const label = isSameDay(date, now)
      ? options.todayLabel
      : isSameDay(date, yesterday)
        ? options.yesterdayLabel
        : options.formatDate(dayKey);

    push(dayKey, label, item);
  }

  return Array.from(buckets.values());
}
